import type { AgGridReact } from "@jsq/ds/core";
import { URL_FILTER_FIELDS, URL_PARAMS, WORKFLOWS_ROUTE } from "../../constants";
import { buildClientUrlFilters } from "../../filterUtils";
import {
  ClientWorkspaceCell,
  ArenaIdCell,
  ClickableCountCell,
  OverdueCell,
  RiskColoredCell,
  WorkflowTypeChips,
} from "./cellRenderers";
import { ClientRowItem } from "./types";

/**
 * Returns the column definitions for the Clients table.
 *
 * @param useWorkflowEngine - When true (crltWorkflowEngine flag ON) filter keys and values
 *   are formatted for the unified WorkflowEngine. When false, the legacy Jira grid format
 *   is used. This value is threaded into cellRendererParams so that click-through navigation
 *   builds the correct URL filter model without requiring feature-flag access in the renderers.
 */
export const getColumnDefs = (
  useWorkflowEngine: boolean,
): AgGridReact.AgGridReactProps["columnDefs"] => {
  const buildWorkflowTypeNavigationUrl = (
    data: ClientRowItem,
    type: { name: string; value: string },
  ): string | null => {
    if (!data?.arenaId) return null;
    const workflowTypeKey = useWorkflowEngine
      ? URL_FILTER_FIELDS.WORKFLOW_TYPE_WE
      : URL_FILTER_FIELDS.WORKFLOW_TYPE;
    const typeFilterValue = useWorkflowEngine ? type.value : type.name;
    const filterModel = {
      ...buildClientUrlFilters(data, useWorkflowEngine),
      [workflowTypeKey]: { filterType: "set", values: [typeFilterValue] },
    };
    const params = new URLSearchParams();
    params.set(URL_PARAMS.FILTERS_PARAM, JSON.stringify(filterModel));
    return `${WORKFLOWS_ROUTE}?${params.toString()}`;
  };

  return [
    {
      headerName: "Client workspace",
      width: 708,
      cellRenderer: ClientWorkspaceCell,
      filter: false,
      field: "arenaName",
    },
    {
      field: "arenaId",
      headerName: "Arena ID",
      width: 160,
      filter: false,
      sortable: false,
      cellRenderer: ArenaIdCell,
    },
    {
      field: "activeWorkflows",
      headerName: "Active workflows",
      width: 160,
      cellRenderer: ClickableCountCell,
      cellRendererParams: { isActiveWorkflows: true, useWorkflowEngine },
      filter: false,
    },
    {
      field: "workflowTypes",
      headerName: "Workflow types",
      flex: 1,
      minWidth: 400,
      cellRenderer: WorkflowTypeChips,
      cellRendererParams: { buildNavigationUrl: buildWorkflowTypeNavigationUrl },
      sortable: false,
      filter: false,
      autoHeight: true,
      wrapText: true,
    },
    {
      field: "dueIn5Days",
      headerName: "Due in next 5 days",
      width: 160,
      cellRenderer: RiskColoredCell,
      cellRendererParams: { dueDateRangeKey: "dueIn5Days", useWorkflowEngine },
      filter: false,
    },
    {
      field: "overdue",
      headerName: "Overdue",
      width: 160,
      cellRenderer: OverdueCell,
      cellRendererParams: { dueDateRangeKey: "overdue", useWorkflowEngine },
      sort: "desc" as const,
      filter: false,
    },
  ];
};
